import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Star, 
  ShoppingCart, 
  Heart, 
  Truck, 
  Shield, 
  Package,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Share2,
  Plus,
  Minus,
  BookOpen,
  Pill,
  Tag,
  MapPin,
  TrendingUp,
  Award,
  Calendar,
   User,
   ThumbsUp,
   Send
 } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { motion } from "framer-motion";
import { useNotifications } from "../contexts/NotificationContext";
import FeatureHeader from "../components/FeatureHeader";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDose, setSelectedDose] = useState("10mg");
  const [activeImage, setActiveImage] = useState("");
  const [newRating, setNewRating] = useState(5);
const [newComment, setNewComment] = useState("");
const [submittingReview, setSubmittingReview] = useState(false);
const [comments, setComments] = useState([]);
const [submittingComment, setSubmittingComment] = useState(false);
const [imageUrl, setImageUrl] = useState("");
const [addingToCart, setAddingToCart] = useState(false);
const [quantity, setQuantity] = useState(1);
const [isWishlisted, setIsWishlisted] = useState(false);
const [activeTab, setActiveTab] = useState("details");

  const handleAddToCart = async () => {
    if (!product) return;

    try {
      setAddingToCart(true);
      const response = await api.post("/api/cart/add", {
        medicineId: product._id,
        dose: selectedDose,
        quantity: quantity,
      });

      // Show success animation or notification
      showAddToCartSuccess();
      addNotification({
        type: "cart_add",
        title: "Added to Cart",
        body: `${product.name} (${selectedDose} × ${quantity}) added to your cart`,
        icon: "🛒",
      });
      console.log("Cart response:", response.data);
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Failed to add to cart. Make sure you are logged in!");
    } finally {
      setAddingToCart(false);
    }
  };

  const showAddToCartSuccess = () => {
    const successEl = document.createElement("div");
    successEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      z-index: 9999;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
      animation: slideIn 0.3s ease;
      font-weight: 600;
    `;
    successEl.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Added to cart successfully!
      </div>
    `;
    document.body.appendChild(successEl);
    setTimeout(() => {
      successEl.style.animation = "slideOut 0.3s ease forwards";
      setTimeout(() => document.body.removeChild(successEl), 300);
    }, 2000);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/api/medicines/${id}`);
        setProduct(response.data.medicine);
        setImageUrl(response.data.medicine.images && response.data.medicine.images.length > 0 ? response.data.medicine.images[0] : '');
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      if (product?.images?.length) setActiveImage(product.images[0]);

      const fetchRelated = async () => {
        try {
          const res = await api.get(`/api/medicines/related/${id}`);
          setRelated(res.data.related);
        } catch (error) {
          console.error("Error fetching related medicines:", error);
        }
      };

      const fetchFeatured = async () => {
        try {
          const res = await api.get("/api/medicines/featured");
          setFeatured(res.data.medicines);
        } catch (error) {
          console.error("Error fetching featured medicines:", error);
        }
      };

      const fetchComments = async () => {
        try {
          const res = await api.get(`/api/medicines/${id}/comments`);
          setComments(res.data.comments || []);
        } catch (error) {
          console.error("Error fetching comments:", error);
        }
      };

      fetchRelated();
      fetchFeatured();
      fetchComments();
    }
  }, [product, id]);

  if (loading) return (
    <div style={styles.loadingContainer}>
      <div style={styles.loader}>
        <div style={styles.spinner}></div>
        <p>Loading product details...</p>
      </div>
    </div>
  );
  
  if (!product) return (
    <div style={styles.errorContainer}>
      <AlertCircle size={48} color="#EF4444" />
      <h2 style={styles.errorTitle}>Product Not Found</h2>
      <p style={styles.errorText}>The product you're looking for doesn't exist or has been removed.</p>
      <button 
        onClick={() => navigate(-1)} 
        style={styles.backButton}
      >
        <ArrowLeft size={18} /> Go Back
      </button>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Custom CSS */}
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOut {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
        `}
      </style>

      {/* Header with Back Button */}
      <motion.div 
   
      >

        <FeatureHeader 
          title="Product Details" 
          subtitle = "Check the details of the product"
          onBack ={() => navigate(-1)}
          />
      
  
        
      </motion.div>

      {/* Product Gallery */}
      <div style={styles.gallerySection}>
        <div style={styles.mainImageContainer}>
          <img
            src={imageUrl}
            alt={product.name}
            style={styles.mainImage}
          />
          <div style={styles.imageOverlay}>
            <span style={styles.imageBadge}>
              <TrendingUp size={14} /> Best Seller
            </span>
          </div>
        </div>
        <div style={styles.thumbnailContainer}>
          {product.images.map((img, i) => (
            <button
              key={i}
              style={{
                ...styles.thumbnail,
                border: activeImage === img ? '2px solid var(--primary)' : '1px solid var(--border)',
              }}
              onClick={() => setActiveImage(img)}
            >
              <img
                src={img}
                alt={`Thumbnail ${i + 1}`}
                style={styles.thumbnailImage}
              />
            </button>
          ))}
        </div>

        <h6>{product.name}</h6>
      </div>

      {/* Product Info Card */}
      <motion.div 
        style={styles.infoCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div style={styles.priceSection}>
          <div>
            <span style={styles.currentPrice}>${product.price}</span>
            {product.originalPrice > product.price && (
              <span style={styles.originalPrice}>${product.originalPrice}</span>
            )}
            <span style={styles.priceUnit}>/tablet</span>
          </div>
          <div style={styles.ratingBadge}>
            <Star size={16} fill="#FFD700" color="#FFD700" />
            <span style={styles.ratingText}>{product.rating}</span>
            <span style={styles.reviewsText}>({product.reviews} reviews)</span>
          </div>
        </div>

        {/* Dosage Selector */}
        {product.stock && (
          <div style={styles.dosageSection}>
            <h3 style={styles.sectionTitle}>Select Dosage</h3>
            <div style={styles.dosageButtons}>
              {Object.keys(product.stock).map((dose) => (
                <button
                  key={dose}
                  style={{
                    ...styles.dosageButton,
                    background: selectedDose === dose ? 'var(--primary)' : 'var(--surface)',
                    color: selectedDose === dose ? 'white' : 'var(--on-bg)',
                  }}
                  onClick={() => setSelectedDose(dose)}
                  disabled={product.stock[dose] === 0}
                >
                  {dose}
                  <span style={styles.stockIndicator}>
                    {product.stock[dose] > 0 ? '✓' : '✗'}
                  </span>
                </button>
              ))}
            </div>
            <p style={styles.stockStatus}>
              {product.stock[selectedDose] > 0 
                ? `🟢 ${product.stock[selectedDose]} tablets available` 
                : '🔴 Out of stock'}
            </p>
          </div>
        )}

        {/* Quantity Selector */}
        <div style={styles.quantitySection}>
          <h3 style={styles.sectionTitle}>Quantity</h3>
          <div style={styles.quantityControls}>
            <button 
              style={styles.quantityButton}
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              <Minus size={20} />
            </button>
            <span style={styles.quantityDisplay}>{quantity}</span>
            <button 
              style={styles.quantityButton}
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus size={20} />
            </button>
            <span style={styles.quantityPrice}>
              Total: ${(product.price * quantity).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Features */}
        <div style={styles.featuresGrid}>
          <div style={styles.feature}>
            <Truck size={20} color="var(--primary)" />
            <span>{product.shipping || "Free Delivery"}</span>
          </div>
          <div style={styles.feature}>
            <Shield size={20} color="var(--primary)" />
            <span>{product.refundPolicy || "30-Day Return"}</span>
          </div>
          <div style={styles.feature}>
            <Package size={20} color="var(--primary)" />
            <span>Verified Pharmacy</span>
          </div>
          <div style={styles.feature}>
            <Clock size={20} color="var(--primary)" />
            <span>Delivery in 2-3 days</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <motion.button
            style={{
              ...styles.addToCartButton,
              opacity: product.stock[selectedDose] === 0 ? 0.6 : 1,
            }}
            disabled={product.stock[selectedDose] === 0 || addingToCart}
            onClick={handleAddToCart}
            whileHover={{ scale: product.stock[selectedDose] > 0 ? 1.02 : 1 }}
            whileTap={{ scale: product.stock[selectedDose] > 0 ? 0.98 : 1 }}
          >
            <ShoppingCart size={20} />
            {addingToCart ? "Adding..." : "Add to Cart"}
          </motion.button>
          
        </div>
      </motion.div>

      {/* Product Details Tabs */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabsHeader}>
          <button
            style={{
              ...styles.tabButton,
              borderBottom: activeTab === "details" ? '3px solid var(--primary)' : 'none',
              color: activeTab === "details" ? 'var(--primary)' : 'var(--on-bg)',
            }}
            onClick={() => setActiveTab("details")}
          >
            <BookOpen size={18} /> Details
          </button>
          <button
            style={{
              ...styles.tabButton,
              borderBottom: activeTab === "directions" ? '3px solid var(--primary)' : 'none',
              color: activeTab === "directions" ? 'var(--primary)' : 'var(--on-bg)',
            }}
            onClick={() => setActiveTab("directions")}
          >
            <Pill size={18} /> Instructions
          </button>
          <button
            style={{
              ...styles.tabButton,
              borderBottom: activeTab === "reviews" ? '3px solid var(--primary)' : 'none',
              color: activeTab === "reviews" ? 'var(--primary)' : 'var(--on-bg)',
            }}
            onClick={() => setActiveTab("reviews")}
          >
            <Star size={18} /> Reviews
          </button>
          <button
            style={{
              ...styles.tabButton,
              borderBottom: activeTab === "pharmacy" ? '3px solid var(--primary)' : 'none',
              color: activeTab === "pharmacy" ? 'var(--primary)' : 'var(--on-bg)',
            }}
            onClick={() => setActiveTab("pharmacy")}
          >
            <MapPin size={18} /> Pharmacy
          </button>
        </div>

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === "details" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 style={styles.contentTitle}>Product Description</h3>
              <p style={styles.description}>{product.description}</p>
              
              <div style={styles.specsGrid}>
                <div style={styles.specItem}>
                  <strong>Generic Name</strong>
                  <span>{product.genericName || "Not specified"}</span>
                </div>
                <div style={styles.specItem}>
                  <strong>Brand Name</strong>
                  <span>{product.brandName || "Not specified"}</span>
                </div>
                <div style={styles.specItem}>
                  <strong>Drug Class</strong>
                  <span>{product.drugClass || "Not specified"}</span>
                </div>
                <div style={styles.specItem}>
                  <strong>Form</strong>
                  <span>{product.form || "Tablet"}</span>
                </div>
                <div style={styles.specItem}>
                  <strong>Manufacturer</strong>
                  <span>{product.manufacturer || "Not specified"}</span>
                </div>
                <div style={styles.specItem}>
                  <strong>Expiry</strong>
                  <span>2 years from manufacture</span>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "directions" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={styles.instructionSection}>
                <h4 style={styles.instructionTitle}>
                  <Calendar size={20} /> Directions of Use
                </h4>
                <ul style={styles.instructionList}>
                  {product.directions && product.directions.length > 0 ? (
                    product.directions.map((item, i) => (
                      <li key={i} style={styles.instructionItem}>
                        <CheckCircle size={16} color="#10B981" />
                        {item}
                      </li>
                    ))
                  ) : (
                    <p>No directions available.</p>
                  )}
                </ul>
              </div>
              <div style={styles.instructionSection}>
                <h4 style={styles.instructionTitle}>
                  <Package size={20} /> Storage Instructions
                </h4>
                <ul style={styles.instructionList}>
                  {product.storageInstructions && product.storageInstructions.length > 0 ? (
                    product.storageInstructions.map((item, i) => (
                      <li key={i} style={styles.instructionItem}>
                        <CheckCircle size={16} color="#10B981" />
                        {item}
                      </li>
                    ))
                  ) : (
                    <p>No storage instructions available.</p>
                  )}
                </ul>
              </div>
            </motion.div>
          )}

          {activeTab === "reviews" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={styles.ratingOverview}>
                <div style={styles.ratingScore}>
                  <h2 style={styles.ratingNumber}>{product.rating}</h2>
                  <div style={styles.starRating}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        fill={i < Math.round(product.rating) ? "#FFD700" : "#E5E7EB"}
                        color={i < Math.round(product.rating) ? "#FFD700" : "#E5E7EB"}
                      />
                    ))}
                  </div>
                  <p style={styles.ratingCount}>{product.reviews} reviews</p>
                </div>
                <div style={styles.reviewForm}>
                  <h4 style={styles.formTitle}>Rate this product</h4>
                  <div style={styles.ratingStars}>
                    {[1,2,3,4,5].map((star) => (
                      <Star
                        key={star}
                        size={24}
                        fill={star <= newRating ? "#FFD700" : "#E5E7EB"}
                        color={star <= newRating ? "#FFD700" : "#E5E7EB"}
                        style={{ cursor: "pointer", margin: '0 2px' }}
                        onClick={() => setNewRating(star)}
                      />
                    ))}
                  </div>
                  <button
                    style={styles.submitReviewButton}
                    disabled={submittingReview}
                    onClick={async () => {
                      try {
                        setSubmittingReview(true);
                        const response = await api.patch(`/api/medicines/${product._id}/review`, {
                          newRating
                        });
                        setProduct(response.data.medicine);
                        setNewRating(5);
                      } catch (err) {
                        console.error(err);
                      } finally {
                        setSubmittingReview(false);
                      }
                    }}
                  >
                    {submittingReview ? "Submitting..." : "Submit Rating"}
                  </button>
                </div>

                <div style={styles.commentSection}>
                  <h4 style={styles.formTitle}>Write a comment</h4>
                  <div style={styles.commentInputWrapper}>
                    <textarea
                      style={styles.commentTextareaModern}
                      placeholder="Share your experience, ask questions, or help other customers..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                    />
                  </div>
                  <div style={styles.commentActions}>
                    <div style={styles.ratingStars}>
                      {[1,2,3,4,5].map((star) => (
                        <Star
                          key={star}
                          size={20}
                          fill={star <= newRating ? "#FFD700" : "#E5E7EB"}
                          color={star <= newRating ? "#FFD700" : "#E5E7EB"}
                          style={{ cursor: "pointer", margin: '0 2px', transition: 'all 0.2s ease', transform: star <= newRating ? 'scale(1.1)' : 'scale(1)' }}
                          onClick={() => setNewRating(star)}
                        />
                      ))}
                    </div>
                    <button
                      style={{
                        ...styles.commentSubmitButton,
                        opacity: !newComment.trim() ? 0.5 : 1,
                      }}
                      disabled={submittingComment || !newComment.trim()}
                      onClick={async () => {
                        try {
                          setSubmittingComment(true);
                          const userId = localStorage.getItem("userId");
                          const userName = localStorage.getItem("userName") || "Anonymous";
                          await api.post(`/api/medicines/${product._id}/comments`, {
                            userId,
                            userName,
                            text: newComment,
                            rating: newRating,
                          });
                          setNewComment("");
                          const res = await api.get(`/api/medicines/${product._id}/comments`);
                          setComments(res.data.comments || []);
                          const updated = await api.get(`/api/medicines/${product._id}`);
                          setProduct(updated.data.medicine);
                        } catch (err) {
                          console.error(err);
                        } finally {
                          setSubmittingComment(false);
                        }
                      }}
                    >
                      {submittingComment ? "Posting..." : (
                        <span style={styles.commentSubmitBtnContent}>
                          <Send size={16} /> Post Comment
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <div style={styles.commentsList}>
                  <h4 style={styles.formTitle}>User Comments</h4>
                  {comments.length === 0 ? (
                    <p style={styles.noComments}>No comments yet. Be the first to share your experience!</p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment._id} style={styles.commentItem}>
                        <div style={styles.commentHeader}>
                          <User size={16} />
                          <span style={styles.commentUser}>{comment.userName || "Anonymous"}</span>
                          {comment.rating && (
                            <div style={styles.commentStarRating}>
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  fill={i < comment.rating ? "#FFD700" : "#E5E7EB"}
                                  color={i < comment.rating ? "#FFD700" : "#E5E7EB"}
                                />
                              ))}
                            </div>
                          )}
                          <span style={styles.commentDate}>
                            {new Date(comment.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p style={styles.commentText}>{comment.text}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "pharmacy" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={styles.pharmacyCard}>
                <div style={styles.pharmacyHeader}>
                  <img
                    src={product.pharmacyLogo || "https://via.placeholder.com/50"}
                    alt={product.pharmacy}
                    style={styles.pharmacyLogo}
                  />
                  <div>
                    <h3 style={styles.pharmacyName}>{product.pharmacy}</h3>
                    <div style={styles.pharmacyRating}>
                      <Star size={16} fill="#FFD700" color="#FFD700" />
                      <span>4.8</span>
                      <span style={styles.ratingText}>(2.4k reviews)</span>
                    </div>
                  </div>
                </div>
                <div style={styles.pharmacyDetails}>
                  <div style={styles.pharmacyDetail}>
                    <Truck size={18} />
                    <span>{product.shipping}</span>
                  </div>
                  <div style={styles.pharmacyDetail}>
                    <Shield size={18} />
                    <span>{product.refundPolicy}</span>
                  </div>
                  <div style={styles.pharmacyDetail}>
                    <CheckCircle size={18} color="#10B981" />
                    <span>Verified Pharmacy</span>
                  </div>
                </div>
                <button style={styles.viewPharmacyButton}>
                  View Pharmacy Details
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <div style={styles.relatedSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <Tag size={20} /> Related Products
            </h2>
            <button style={styles.viewAllButton}>
              View All <ChevronRight size={16} />
            </button>
          </div>
          <div style={styles.relatedGrid}>
            {related.slice(0, 4).map((item) => (
              <motion.div
                key={item._id}
                style={styles.relatedCard}
                whileHover={{ y: -4 }}
                onClick={() => navigate(`/product/${item._id}`)}
              >
                <div style={styles.relatedImageContainer}>
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    style={styles.relatedImage}
                  />
                  {item.featured && (
                    <span style={styles.relatedBadge}>
                      <Award size={12} /> Featured
                    </span>
                  )}
                </div>
                <div style={styles.relatedInfo}>
                  <h4 style={styles.relatedName}>{item.name}</h4>
                  <div style={styles.relatedPrice}>
                    <span style={styles.relatedCurrentPrice}>${item.price}</span>
                    {item.originalPrice > item.price && (
                      <span style={styles.relatedOriginalPrice}>${item.originalPrice}</span>
                    )}
                  </div>
                  <div style={styles.relatedRating}>
                    <Star size={12} fill="#FFD700" color="#FFD700" />
                    <span>{item.rating}</span>
                    <span>({item.reviews})</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Premium Styling
const styles = {
  container: {
    backgroundColor: 'var(--bg)',
    minHeight: '100vh',
    paddingBottom: '100px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--bg)',
  },
  loader: {
    textAlign: 'center',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid var(--border)',
    borderTop: '4px solid var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '24px',
    textAlign: 'center',
    backgroundColor: 'var(--bg)',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    margin: '16px 0 8px',
  },
  errorText: {
    color: 'var(--muted)',
    marginBottom: '24px',
    maxWidth: '400px',
  },
  backButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: 'var(--surface)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerBackButton: {
    background: 'none',
    border: 'none',
    color: 'var(--on-bg)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    marginLeft: '12px',
  },
  productNameHeader: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    margin: '0 0 4px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerBadges: {
    display: 'flex',
    gap: '8px',
  },
  badgeRx: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#EF4444',
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '12px',
    fontWeight: '600',
  },
  badgeFeatured: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10B981',
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '12px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: 'var(--on-bg)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gallerySection: {
    padding: '20px',
  },
  mainImageContainer: {
    position: 'relative',
    height: '300px',
    borderRadius: '24px',
    overflow: 'hidden',
    backgroundColor: 'white',
    marginBottom: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  },
  mainImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '20px',
  },
  imageOverlay: {
    position: 'absolute',
    top: '16px',
    left: '16px',
  },
  imageBadge: {
    backgroundColor: 'var(--primary)',
    color: 'white',
    fontSize: '12px',
    padding: '6px 12px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: '600',
    backdropFilter: 'blur(10px)',
  },
  thumbnailContainer: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    padding: '8px 0',
  },
  thumbnail: {
    flexShrink: 0,
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'white',
    padding: '4px',
    cursor: 'pointer',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  infoCard: {
    backgroundColor: 'var(--surface)',
    margin: '0 20px',
    padding: '24px',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    border: '1px solid var(--border)',
  },
  priceSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  currentPrice: {
    fontSize: '32px',
    fontWeight: '800',
    color: 'var(--primary)',
    marginRight: '8px',
  },
  originalPrice: {
    fontSize: '18px',
    color: 'var(--muted)',
    textDecoration: 'line-through',
    marginRight: '8px',
  },
  priceUnit: {
    fontSize: '14px',
    color: 'var(--muted)',
  },
  ratingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
    padding: '6px 12px',
    borderRadius: '20px',
  },
  ratingText: {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--on-bg)',
  },
  reviewsText: {
    fontSize: '12px',
    color: 'var(--muted)',
  },
  dosageSection: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    marginBottom: '12px',
  },
  dosageButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  dosageButton: {
    padding: '10px 16px',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  },
  stockIndicator: {
    fontSize: '12px',
  },
  stockStatus: {
    fontSize: '13px',
    color: 'var(--on-bg)',
    marginTop: '8px',
  },
  quantitySection: {
    marginBottom: '24px',
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  quantityButton: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--on-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
  },
  quantityDisplay: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    minWidth: '30px',
    textAlign: 'center',
  },
  quantityPrice: {
    marginLeft: 'auto',
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--primary)',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '24px',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--on-bg)',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  addToCartButton: {
    flex: 2,
    padding: '16px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buyNowButton: {
    flex: 1,
    padding: '16px',
    backgroundColor: 'var(--surface)',
    color: 'var(--primary)',
    border: '2px solid var(--primary)',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  tabsContainer: {
    margin: '24px 20px',
    backgroundColor: 'var(--surface)',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    border: '1px solid var(--border)',
  },
  tabsHeader: {
    display: 'flex',
    borderBottom: '1px solid var(--border)',
    overflowX: 'auto',
  },
  tabButton: {
    flex: 1,
    padding: '20px',
    background: 'none',
    border: 'none',
    color: 'var(--on-bg)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    minWidth: '120px',
  },
  tabContent: {
    padding: '24px',
  },
  contentTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    marginBottom: '16px',
  },
  description: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: 'var(--on-bg)',
    marginBottom: '24px',
  },
  specsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  specItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  specItem : {
    fontSize: '13px',
    color: 'var(--muted)',
  },
  instructionSection: {
    marginBottom: '24px',
  },
  instructionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  instructionList: {
    listStyle: 'none',
    padding: 0,
  },
  instructionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
    fontSize: '14px',
    color: 'var(--on-bg)',
  },
  ratingOverview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  ratingScore: {
    textAlign: 'center',
  },
  ratingNumber: {
    fontSize: '48px',
    fontWeight: '800',
    color: 'var(--on-bg)',
    margin: 0,
  },
  starRating: {
    display: 'flex',
    justifyContent: 'center',
    gap: '2px',
    margin: '8px 0',
  },
  ratingCount: {
    fontSize: '14px',
    color: 'var(--muted)',
  },
  reviewForm: {
    backgroundColor: 'rgba(var(--primary-rgb), 0.05)',
    padding: '20px',
    borderRadius: '16px',
  },
  commentSection: {
    backgroundColor: 'var(--surface)',
    padding: '24px',
    borderRadius: '20px',
    border: '1px solid var(--border)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
    marginTop: '16px',
  },
  commentInputWrapper: {
    position: 'relative',
    marginBottom: '16px',
  },
  commentTextarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid var(--primary)',
    backgroundColor: 'var(--surface)',
    color: 'var(--on-bg)',
    fontSize: '14px',
    resize: 'vertical',
    marginBottom: '12px',
    minHeight: '100px',
  },
  commentTextareaModern: {
    width: '100%',
    padding: '16px',
    borderRadius: '16px',
    border: '2px solid var(--primary)',
    backgroundColor: 'rgba(var(--primary-rgb), 0.02)',
    color: 'var(--on-bg)',
    fontSize: '14px',
    resize: 'vertical',
    outline: 'none',
    transition: 'all 0.3s ease',
    minHeight: '100px',
    fontFamily: 'inherit',
  },
  commentActions: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  commentsList: {
    marginTop: '24px',
  },
  noComments: {
    fontSize: '14px',
    color: 'var(--muted)',
    textAlign: 'center',
    padding: '32px 16px',
    fontStyle: 'italic',
  },
  commentItem: {
    backgroundColor: 'var(--surface)',
    padding: '16px',
    borderRadius: '16px',
    marginBottom: '12px',
    border: '1px solid var(--border)',
    borderLeft: '3px solid var(--primary)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
    transition: 'all 0.2s ease',
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  commentUser: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--on-bg)',
  },
  commentStarRating: {
    display: 'flex',
    gap: '2px',
  },
  commentDate: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: 'var(--muted)',
  },
  commentText: {
    fontSize: '14px',
    color: 'var(--on-bg)',
    lineHeight: '1.5',
  },
  commentSubmitButton: {
    padding: '10px 24px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)',
    minWidth: '120px',
  },
  commentSubmitBtnContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    marginBottom: '12px',
  },
  ratingStars: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  submitReviewButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  pharmacyCard: {
    backgroundColor: 'rgba(var(--primary-rgb), 0.05)',
    padding: '24px',
    borderRadius: '20px',
  },
  pharmacyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
  },
  pharmacyLogo: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    objectFit: 'cover',
  },
  pharmacyName: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    margin: '0 0 4px 0',
  },
  pharmacyRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: 'var(--on-bg)',
  },
  pharmacyDetails: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '12px',
    marginBottom: '20px',
  },
  pharmacyDetail: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--on-bg)',
  },
  viewPharmacyButton: {
    padding: '12px',
    backgroundColor: 'var(--surface)',
    color: 'var(--primary)',
    border: '2px solid var(--primary)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  relatedSection: {
    margin: '32px 20px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--on-bg)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
  },
  viewAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  relatedCard: {
    backgroundColor: 'var(--surface)',
    borderRadius: '20px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: '1px solid var(--border)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
  relatedImageContainer: {
    position: 'relative',
    height: '120px',
    backgroundColor: 'white',
  },
  relatedImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '16px',
  },
  relatedBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(16, 185, 129, 0.9)',
    color: 'white',
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontWeight: '600',
  },
  relatedInfo: {
    padding: '16px',
  },
  relatedName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--on-bg)',
    margin: '0 0 8px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  relatedPrice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  relatedCurrentPrice: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--primary)',
  },
  relatedOriginalPrice: {
    fontSize: '12px',
    color: 'var(--muted)',
    textDecoration: 'line-through',
  },
  relatedRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: 'var(--on-bg)',
  },
};